import { MonetizeNowLogo } from '@monetize/ui/icons';
import { FC } from 'react';
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md';
import { useAtom } from 'jotai';
import { AppMenu } from '../components/App/AppMenu';
import AppMenuTenantSwitch from '../components/App/AppMenuTenantSwitch';
import { AppHelpSearch } from '../components/AppSupport/AppHelpSearch';
import { MBox, MFlex, MIcon, MText, MTooltip } from '../components/Monetize';
import { APP_SIDEBAR_WIDTH } from '../constants/layout';
import { useAuth } from '../services/auth0';
import { appLayoutState } from '../store/global.store';
import { ProfileNav } from './ProfileNav';

const HoverLogo = ({
  isOverlay,
  toggleSidebar,
}: {
  isOverlay: boolean;
  toggleSidebar: () => void;
}) => {
  return (
    <MTooltip label={isOverlay ? 'Expand' : 'Collapse'} placement="bottom-end">
      <MFlex
        as="button"
        aria-label="Toggle sidebar"
        aria-expanded={!isOverlay}
        className="group"
        alignItems="center"
        justifyContent="center"
        h={9}
        w={9}
        onClick={toggleSidebar}
        bg="tIndigo.base"
        border="none"
        borderRadius="md"
        cursor="pointer"
        position="relative"
        _focusVisible={{
          outline: 'none',
          boxShadow: 'focus',
        }}
      >
        <MonetizeNowLogo height={20} width={24} aria-hidden="true" />
        <MBox
          position="absolute"
          inset="0"
          borderRadius="md"
          display="none"
          alignItems="center"
          justifyContent="center"
          bg="tIndigo.base"
          _groupHover={{ display: 'flex' }}
          _groupFocusVisible={{ display: 'flex' }}
        >
          <MIcon
            aria-hidden="true"
            as={isOverlay ? MdKeyboardArrowRight : MdKeyboardArrowLeft}
            color="tWhite.base"
            w={6}
            h={6}
          />
        </MBox>
      </MFlex>
    </MTooltip>
  );
};

export const AppSidebar: FC = () => {
  const { user, currentTenantUser } = useAuth();
  const [layoutMode, setLayoutMode] = useAtom(appLayoutState);

  const isOverlay = layoutMode === 'overlay';

  const toggleSidebar = () => {
    if (isOverlay) {
      setLayoutMode('static');
      return;
    }
    setLayoutMode('overlay');
  };

  const renderNavLogo = () => {
    return (
      <MFlex
        alignItems="center"
        justifyContent={isOverlay ? 'center' : 'flex-start'}
        h={14}
        mb={2}
        px={isOverlay ? 0 : 4}
      >
        <HoverLogo isOverlay={isOverlay} toggleSidebar={toggleSidebar} />
        {!isOverlay && (
          <MText
            pl={3}
            fontSize="md"
            fontWeight="semibold"
            color="tPurple.dark"
          >
            MonetizeNow
          </MText>
        )}
      </MFlex>
    );
  };

  return (
    <MFlex
      position="fixed"
      direction="column"
      width={`${APP_SIDEBAR_WIDTH[layoutMode]}px`}
      maxW={`${APP_SIDEBAR_WIDTH[layoutMode]}px`}
      height="full"
      bgColor="tWhite.base"
      boxShadow="sideNavbar"
      pt={3}
      pb={2}
    >
      <MBox maxW="inherit" flexShrink={0}>
        {renderNavLogo()}
        <AppMenu layoutMode={layoutMode} />
      </MBox>

      <MBox flex="1" maxW="inherit">
        {isOverlay && (
          <MBox h="100%" cursor="pointer" onClick={toggleSidebar} />
        )}
      </MBox>

      <MFlex direction="column" gap={1} flexShrink={0}>
        <AppHelpSearch isOverlay={isOverlay} />
        <AppMenuTenantSwitch isOverlay={isOverlay} />
        <ProfileNav
          isOverlay={isOverlay}
          user={user}
          currentTenantUser={currentTenantUser}
        />
      </MFlex>
    </MFlex>
  );
};
